import type { CertificatePdfData } from '@components/certificate-pdf/index.ts';
import { DATE_FORMATS } from '@constants/date.ts';
import type { CreatorElement } from '@routes/sidebar/admin-center/creator/___pages___/elements/api/creatorElements.api.ts';
import {
    CERTIFICATE_PREVIEW_LEARNER,
    CERTIFICATE_PREVIEW_NO_TITLE,
    CERTIFICATE_PREVIEW_NO_VERSION,
    CERTIFICATE_PREVIEW_STORAGE_KEY_PREFIX,
} from '@routes/sidebar/admin-center/creator/___pages___/pathways/___pages___/certificate-preview/constants.ts';
import type {
    PathwaySectionRequest,
    PathwaySectionResponse,
} from '@routes/sidebar/admin-center/creator/___pages___/pathways/api/creatorPathways.api.ts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import sortBy from 'lodash/sortBy';

dayjs.extend(utc);

export interface CertificatePreviewLearner {
    firstName: string;
    lastName: string;
    dateOfBirthUTC: string | null;
    nationality: string | null;
}

export interface CertificatePreviewDraft {
    title: string;
    version: string | null;
    certificateText: string | null;
    learningObjectives: string[];
    learner: CertificatePreviewLearner | null;
}

interface StoredCertificatePreviewDraft {
    createdAt: number;
    draft: CertificatePreviewDraft;
}

const collectSavedElements = (sections: PathwaySectionResponse[], elements: Map<number, CreatorElement>) => {
    sections.forEach((section) => {
        section.elements.forEach((element) => {
            const info = element.elementAdmin.elementAdminInfo;
            elements.set(info.id, info);
        });
        collectSavedElements(section.subSections ?? [], elements);
    });
};

const getSectionElementIds = (section: PathwaySectionRequest): number[] => [
    ...sortBy(section.elements, (element) => element.elementOrder).map((element) => element.id),
    ...sortBy(section.subSections, (subSection) => subSection.sectionOrder).flatMap(getSectionElementIds),
];

export const getCertificatePreviewObjectives = (
    sections: PathwaySectionRequest[],
    savedSections: PathwaySectionResponse[],
    addedElements: CreatorElement[],
): string[] => {
    const elementsById = new Map<number, CreatorElement>();
    collectSavedElements(savedSections, elementsById);
    addedElements.forEach((element) => elementsById.set(element.id, element));

    return sortBy(sections, (section) => section.sectionOrder)
        .flatMap(getSectionElementIds)
        .map((id) => elementsById.get(id)?.learningObjective?.trim() ?? '')
        .filter((objective) => objective !== '');
};

const CERTIFICATE_DATE_FORMAT = DATE_FORMATS.PROFESSIONAL;

const formatPreviewDateOfBirth = (dateOfBirthUTC: string | null): string | null => {
    if (!dateOfBirthUTC) return null;
    const date = dayjs.utc(dateOfBirthUTC);
    return date.isValid() ? date.format(CERTIFICATE_DATE_FORMAT) : null;
};

const getPreviewNationality = (isoCode: string | null): string | null => {
    const code = isoCode?.trim();
    if (!code) return null;
    try {
        return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) ?? code;
    } catch {
        return code;
    }
};

export const buildCertificatePreviewData = (
    draft: CertificatePreviewDraft | null,
    qrCodeUrl: string | null,
): CertificatePdfData => ({
    ...CERTIFICATE_PREVIEW_LEARNER,
    certificateVersion: CERTIFICATE_PREVIEW_NO_VERSION,
    fullName:
        [draft?.learner?.firstName, draft?.learner?.lastName]
            .map((name) => name?.trim())
            .filter(Boolean)
            .join(' ') || CERTIFICATE_PREVIEW_LEARNER.fullName,
    dateOfBirth:
        formatPreviewDateOfBirth(draft?.learner?.dateOfBirthUTC ?? null) || CERTIFICATE_PREVIEW_LEARNER.dateOfBirth,
    nationality: getPreviewNationality(draft?.learner?.nationality ?? null) || CERTIFICATE_PREVIEW_LEARNER.nationality,
    issueDate: dayjs.utc().format(CERTIFICATE_DATE_FORMAT),
    pathwayTitle: draft?.title.trim() || CERTIFICATE_PREVIEW_NO_TITLE,
    pathwayVersion: draft?.version ?? CERTIFICATE_PREVIEW_NO_VERSION,
    learningObjectives: draft?.learningObjectives ?? [],
    additionalText: draft?.certificateText?.trim() || null,
    qrCodeUrl,
});

// Hand-over entries older than this are removed (for example when the preview tab never opened).
const CERTIFICATE_PREVIEW_STORAGE_TTL_MS = 60 * 60 * 1000;

const removeExpiredDrafts = () => {
    const now = Date.now();
    Object.keys(localStorage)
        .filter((key) => key.startsWith(CERTIFICATE_PREVIEW_STORAGE_KEY_PREFIX))
        .forEach((key) => {
            try {
                const stored = JSON.parse(localStorage.getItem(key) ?? '') as StoredCertificatePreviewDraft;
                if (now - stored.createdAt > CERTIFICATE_PREVIEW_STORAGE_TTL_MS) localStorage.removeItem(key);
            } catch {
                localStorage.removeItem(key);
            }
        });
};

export const saveCertificatePreviewDraft = (draft: CertificatePreviewDraft): string | null => {
    try {
        removeExpiredDrafts();
        const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const stored: StoredCertificatePreviewDraft = { createdAt: Date.now(), draft };
        localStorage.setItem(`${CERTIFICATE_PREVIEW_STORAGE_KEY_PREFIX}${id}`, JSON.stringify(stored));
        return id;
    } catch {
        return null;
    }
};

export const readCertificatePreviewDraft = (id: string | null): CertificatePreviewDraft | null => {
    if (!id) return null;

    const key = `${CERTIFICATE_PREVIEW_STORAGE_KEY_PREFIX}${id}`;
    try {
        const value = sessionStorage.getItem(key) ?? localStorage.getItem(key);
        if (!value) return null;

        sessionStorage.setItem(key, value);
        localStorage.removeItem(key);
        return (JSON.parse(value) as StoredCertificatePreviewDraft).draft;
    } catch {
        return null;
    }
};
